import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Database, 
  Eye,
  ArrowRight,
  AlertTriangle,
  CheckCircle,
  X,
  Table2,
  Info,
  Loader2,
  Key,
  Link2,
  BarChart3
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNotification } from '../context/NotificationContext';
import { apiService } from '../services/apiService';
import ProcessLoadingScreen from '../components/ProcessLoadingScreen';

interface ColumnEDA {
  column_name: string;
  data_type: string;
  total_count: number;
  non_null_count: number;
  null_count: number;
  null_percentage: number;
  unique_count: number;
  unique_percentage: number;
  is_numeric: boolean;
  min?: number;
  max?: number;
  mean?: number;
  median?: number;
  std?: number;
  variance?: number;
  mode?: (string | number)[];
  percentiles?: {
    p25: number;
    p50: number;
    p75: number;
    p95: number;
    p99: number;
  };
  skewness?: number;
  kurtosis?: number;
  histogram?: {
    counts: number[];
    bin_edges: number[];
  };
  top_values?: Array<{value: string; count: number; percentage: number}>;
}

interface TableInfo {
  name: string;
  numColumns?: number;
  primaryKey?: string;
  columns?: any[];
}

const TableSelectionPage: React.FC = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const { dispatch } = useApp();
  const { showSuccess, showError } = useNotification();
  
  const [runData, setRunData] = useState<any>(null);
  const [schema, setSchema] = useState<any>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [dependencyInfo, setDependencyInfo] = useState<any>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [codeGenStep, setCodeGenStep] = useState(0);
  const [activeTab, setActiveTab] = useState<'schema' | 'preview'>('schema');
  const [error, setError] = useState<string | null>(null);
  
  // Column EDA
  const [showEDAModal, setShowEDAModal] = useState(false);
  const [columnEDA, setColumnEDA] = useState<ColumnEDA | null>(null);
  const [isLoadingEDA, setIsLoadingEDA] = useState(false);
  const [edaTableName, setEdaTableName] = useState('');

  useEffect(() => {
    if (runId) {
      loadRunData();
    }
  }, [runId]);

  useEffect(() => {
    // Load preview when switching to preview tab or when selected table changes
    if (activeTab === 'preview' && selectedTable && runId) {
      loadPreviewData(selectedTable);
    }
  }, [activeTab, selectedTable, runId]);

  const loadRunData = async () => {
    if (!runId) return;
    
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiService.getRun(runId);
      setRunData(data);
      setSchema(data.schema || null);
      
      // Build tables list
      const tableList: TableInfo[] = [];
      if (data.schema && data.schema.tables && Array.isArray(data.schema.tables)) {
        data.schema.tables.forEach((table: any) => {
          tableList.push({
            name: table.table_name || table.name,
            numColumns: table.num_columns || table.columns?.length || 0,
            primaryKey: table.primary_key,
            columns: table.columns
          });
        });
      } else if (data.input_csv_files && data.input_csv_files.length > 0) {
        data.input_csv_files.forEach((filename: string) => {
          tableList.push({
            name: filename.replace('.csv', ''),
            numColumns: 0
          });
        });
      }
      
      setTables(tableList);
      if (tableList.length > 0) {
        setSelectedTable(tableList[0].name);
      }
      
      // Set current run in context
      dispatch({ type: 'SET_CURRENT_RUN', payload: data });
    } catch (err) {
      console.error('Failed to load run data:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to load run data';
      setError(errorMessage);
      showError('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const loadPreviewData = async (tableName: string) => {
    if (!runId) return;
    
    setIsLoadingPreview(true);
    try {
      const filename = `${tableName}.csv`;
      let data;
      try {
        data = await apiService.previewCSV(runId, filename);
      } catch (err) {
        try {
          data = await apiService.previewCSVFromRoot(filename);
        } catch (err2) {
          throw new Error('CSV file not found');
        }
      }
      setPreviewData(data);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setPreviewData(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleTableSelect = (tableName: string) => {
    setSelectedTable(tableName);
  };

  const handleTableToggle = (tableName: string) => {
    setSelectedTables(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tableName)) {
        newSet.delete(tableName);
      } else {
        newSet.add(tableName);
      }
      return newSet;
    });
  };

  // Column EDA handler
  const handleColumnEDA = async (tableName: string, columnName: string) => {
    if (!runId) return;
    
    setIsLoadingEDA(true);
    setShowEDAModal(true);
    setEdaTableName(tableName);
    setColumnEDA(null);
    
    try {
      const result = await apiService.getColumnEDA(runId, tableName, columnName);
      setColumnEDA(result);
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to load column EDA');
      setShowEDAModal(false);
    } finally {
      setIsLoadingEDA(false);
    }
  };

  const handleNext = async () => {
    if (selectedTables.size === 0) {
      showError('Error', 'Please select at least one table');
      return;
    }

    if (!runId) return;

    // Check dependencies if schema exists
    if (schema && schema.relationships) {
      try {
        const deps = await apiService.checkDependencies(runId, Array.from(selectedTables));
        
        if (deps && deps.has_dependencies) {
          setDependencyInfo(deps);
          setShowDependencyModal(true);
          return;
        }
      } catch (err) {
        console.error('Failed to check dependencies:', err);
      }
    }
    
    await generateCodeAndProceed();
  };

  const generateCodeAndProceed = async () => {
    if (!runId) return;
    
    setIsGeneratingCode(true);
    setCodeGenStep(0);
    
    const steps = [
      'Analyzing selected tables',
      'Checking dependencies',
      'Generating Python code',
      'Applying table relationships',
      'Finalizing code generation'
    ];
    
    const stepInterval = setInterval(() => {
      setCodeGenStep(prev => (prev + 1) % steps.length);
    }, 1200);
    
    try {
      let result;
      try {
        result = await apiService.generateCode(runId, Array.from(selectedTables));
      } catch (error) {
        clearInterval(stepInterval);
        setIsGeneratingCode(false);
        
        // Check if it's an API response error with detailed info
        let errorMessage = 'Failed to generate code';
        let actionableSteps: string[] = [];
        
        if (error instanceof Error) {
          errorMessage = error.message;
          
          // Try to extract actionable steps from error message if it's structured
          try {
            const errorData = JSON.parse(error.message);
            if (errorData.actionable_steps) {
              actionableSteps = errorData.actionable_steps;
            }
            if (errorData.error_type === 'openai_quota_exceeded') {
              errorMessage = 'OpenAI API Quota Exceeded';
              if (!actionableSteps.length) {
                actionableSteps = [
                  'Check your OpenAI account billing and quota',
                  'Upgrade your OpenAI plan if needed',
                  'Wait a few minutes and try again',
                  'Check your API usage at platform.openai.com/usage'
                ];
              }
            }
          } catch {
            // Not JSON, use the error message as is
          }
        }
        
        console.error('Code generation error:', error);
        
        let fullErrorMessage = errorMessage;
        if (actionableSteps.length > 0) {
          fullErrorMessage += '\n\nWhat you can do:\n' + actionableSteps.map((step, idx) => `${idx + 1}. ${step}`).join('\n');
        }
        
        showError('Code Generation Failed', fullErrorMessage);
        setError(`Failed to generate code: ${errorMessage}`);
        return;
      }
      
      clearInterval(stepInterval);
      
      if (result && result.success) {
        const message = result.required_tables_added && result.required_tables_added.length > 0
          ? `Code generated! Added ${result.required_tables_added.length} required table(s).`
          : 'Code generated successfully';
        
        // Check for validation issues
        if (result.validation) {
          const validation = result.validation;
          if (validation.has_errors) {
            let validationMessage = validation.message || 'Code validation found issues';
            
            if (validation.issues && validation.issues.length > 0) {
              validationMessage += '\n\nIssues found:';
              validation.issues.slice(0, 5).forEach((issue: any, idx: number) => {
                validationMessage += `\n${idx + 1}. [${issue.severity?.toUpperCase() || 'ISSUE'}] ${issue.message || issue.type || 'Unknown issue'}`;
              });
              if (validation.issues.length > 5) {
                validationMessage += `\n... and ${validation.issues.length - 5} more issue(s)`;
              }
            }
            
            if (validation.actionable_steps && validation.actionable_steps.length > 0) {
              validationMessage += '\n\nWhat you can do:';
              validation.actionable_steps.forEach((step: string, idx: number) => {
                validationMessage += `\n${idx + 1}. ${step}`;
              });
            }
            
            if (validation.was_fixed) {
              validationMessage += '\n\n✅ Code was automatically fixed. You can proceed with data generation.';
              showSuccess('Code Generated (Fixed)', validationMessage);
            } else {
              validationMessage += '\n\n⚠️ Code could not be automatically fixed. Please review the issues above.';
              showError('Code Generated with Issues', validationMessage);
            }
          } else {
            showSuccess('Success', message);
          }
        } else {
          showSuccess('Success', message);
        }
        navigate(`/run/${runId}/generate`);
      } else {
        const errorDetails = result?.error || 'Failed to generate code';
        const actionableSteps = result?.actionable_steps || [];
        let errorMessage = errorDetails;
        if (actionableSteps.length > 0) {
          errorMessage += '\n\nWhat you can do:\n' + actionableSteps.map((step: string, idx: number) => `${idx + 1}. ${step}`).join('\n');
        }
        showError('Code Generation Failed', errorMessage);
      }
    } catch (err) {
      console.error('Failed to generate code:', err);
      clearInterval(stepInterval);
      showError('Error', err instanceof Error ? err.message : 'Failed to generate code');
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleAddDependencies = () => {
    if (dependencyInfo) {
      const newSelected = new Set(selectedTables);
      dependencyInfo.required_tables.forEach((table: string) => {
        newSelected.add(table);
      });
      setSelectedTables(newSelected);
    }
    setShowDependencyModal(false);
    generateCodeAndProceed();
  };

  const handleContinueAnyway = () => {
    setShowDependencyModal(false);
    generateCodeAndProceed();
  };

  const getSelectedTableData = (): TableInfo | undefined => {
    return tables.find(t => t.name === selectedTable);
  };

  // Render loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-center py-12" style={{ height: 'calc(100vh - 72px)' }}>
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-500 dark:text-gray-400">Loading run data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !runData) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex items-center justify-center py-12" style={{ height: 'calc(100vh - 72px)' }}>
          <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-red-200 dark:border-red-800 text-center">
            <div className="text-red-600 dark:text-red-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">Error Loading Data</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <button 
              onClick={loadRunData} 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const selectedTableInfo = getSelectedTableData();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <main className="p-6 overflow-hidden" style={{ height: 'calc(100vh - 72px)' }}>
        <div className="h-full flex flex-col">
          {/* Error Banner */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Table Selection Bar - Compact at top */}
            <div className="flex-shrink-0">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    <h2 className="font-semibold text-gray-900 dark:text-gray-100">Tables</h2>
                    <span className="text-sm text-gray-500">
                      ({selectedTables.size} selected)
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {runData?.synthetic_csv_files && runData.synthetic_csv_files.length > 0 && (
                      <button
                        onClick={() => navigate(`/run/${runId}/generate`)}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 flex items-center space-x-2 text-sm font-medium transition-colors"
                      >
                        <BarChart3 className="h-4 w-4" />
                        <span>View Generated Data</span>
                      </button>
                    )}
                    <button
                      onClick={handleNext}
                      disabled={selectedTables.size === 0 || isGeneratingCode}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 text-sm font-medium transition-colors"
                    >
                      {isGeneratingCode ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Generating...</span>
                        </>
                      ) : (
                        <>
                          <span>Generate Code</span>
                          <ArrowRight className="h-4 w-4" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {/* Horizontal scrollable table buttons */}
                <div className="flex flex-wrap gap-2">
                  {tables.map((table) => {
                    const isActive = selectedTable === table.name;
                    const isChecked = selectedTables.has(table.name);
                    
                    return (
                      <div
                        key={table.name}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                          isActive 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        onClick={() => handleTableSelect(table.name)}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleTableToggle(table.name);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <Table2 className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${isActive ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          {table.name}
                        </span>
                        {table.numColumns && table.numColumns > 0 && (
                          <span className="text-xs text-gray-400">
                            ({table.numColumns} cols)
                          </span>
                        )}
                        {isChecked && (
                          <CheckCircle className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 min-h-0 flex flex-col">
              {/* Tabs */}
              <div className="mb-3 border-b border-gray-200 flex-shrink-0">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                  <button
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'schema' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    onClick={() => setActiveTab('schema')}
                  >
                    Schema
                  </button>
                  <button
                    className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'preview' 
                        ? 'border-blue-600 text-blue-600' 
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    onClick={() => setActiveTab('preview')}
                  >
                    Preview
                  </button>
                </nav>
              </div>

              {/* Content Area */}
              <div className="flex-1 min-h-0 overflow-auto">
                {!selectedTable ? (
                  <div className="bg-white dark:bg-gray-800 p-8 rounded-lg border border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-gray-500">Select a table to view its schema and data</p>
                  </div>
                ) : activeTab === 'schema' ? (
                  // Schema Tab Content
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden h-full">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Table2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{selectedTable}</h3>
                            {selectedTableInfo?.primaryKey && (
                              <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full font-medium flex items-center space-x-1">
                                <Key size={12} />
                                <span>PK: {selectedTableInfo.primaryKey}</span>
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {selectedTableInfo?.numColumns || 0} columns
                            </span>
                          </div>
                        </div>
                        
                        {/* Foreign Keys Display */}
                        {(() => {
                          const foreignKeys = schema?.relationships?.foreign_keys?.filter(
                            (fk: any) => fk.child_table === selectedTable
                          ) || [];
                          
                          if (foreignKeys.length > 0) {
                            return (
                              <div className="flex items-center flex-wrap gap-2">
                                <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center space-x-1">
                                  <Link2 size={14} className="text-amber-600 dark:text-amber-400" />
                                  <span>Foreign Keys:</span>
                                </span>
                                {foreignKeys.map((fk: any, idx: number) => (
                                  <span
                                    key={idx}
                                    className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs rounded-full font-medium"
                                  >
                                    {fk.child_column} → {fk.parent_table}.{fk.parent_column}
                                  </span>
                                ))}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                    
                    {selectedTableInfo?.columns && selectedTableInfo.columns.length > 0 ? (
                      <div className="overflow-auto" style={{ maxHeight: 'calc(100% - 60px)' }}>
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0 z-10">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                                Column Name
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                                Data Type
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                                Nullable
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">
                                Value Range / Info
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {selectedTableInfo.columns.map((col: any, idx: number) => (
                              <tr key={col.name || idx} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {col.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">
                                    {col.data_type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                  {col.nullable ? (
                                    <span className="text-yellow-600 dark:text-yellow-400">Yes</span>
                                  ) : (
                                    <span className="text-green-600 dark:text-green-400">No</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                                  {col.value_range ? (
                                    <div className="space-y-1">
                                      {col.value_range.min !== undefined && col.value_range.max !== undefined && (
                                        <div>Range: {col.value_range.min} - {col.value_range.max}</div>
                                      )}
                                      {col.value_range.mean !== undefined && (
                                        <div>Mean: {typeof col.value_range.mean === 'number' ? col.value_range.mean.toFixed(2) : col.value_range.mean}</div>
                                      )}
                                      {col.value_range.possible_values && (
                                        <div className="flex flex-wrap gap-1">
                                          {col.value_range.possible_values.slice(0, 5).map((val: any, i: number) => (
                                            <span key={i} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                                              {String(val)}
                                            </span>
                                          ))}
                                          {col.value_range.possible_values.length > 5 && (
                                            <span className="text-xs text-gray-400">
                                              +{col.value_range.possible_values.length - 5} more
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-400">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <Info className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No schema details available for this table.</p>
                        <p className="text-sm text-gray-400 mt-1">Try viewing the Preview tab to see the data.</p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Preview Tab Content
                  <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden h-full">
                    {isLoadingPreview ? (
                      <div className="p-8 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-500">Loading preview data...</p>
                      </div>
                    ) : previewData ? (
                      <>
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Eye className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{selectedTable}</h3>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                              <span>{previewData.total_rows?.toLocaleString() || 0} rows</span>
                              <span>{previewData.columns?.length || 0} columns</span>
                            </div>
                          </div>
                        </div>
                        <div className="overflow-auto" style={{ maxHeight: 'calc(100% - 60px)' }}>
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                              <tr>
                                {previewData.columns?.map((col: string) => {
                                  // Get column info from schema to check nullable status
                                  const getColumnInfo = () => {
                                    if (!schema?.tables) return null;
                                    for (const table of schema.tables) {
                                      if (table.table_name === selectedTable || table.name === selectedTable) {
                                        const column = table.columns?.find((c: any) => c.name === col);
                                        return column;
                                      }
                                    }
                                    return null;
                                  };
                                  
                                  const columnInfo = getColumnInfo();
                                  const isNullable = columnInfo?.nullable !== false; // Default to true if not specified
                                  
                                  return (
                                    <th 
                                      key={col} 
                                      className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700 whitespace-nowrap"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <span>{col}</span>
                                        {isNullable ? (
                                          <span className="px-1.5 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs font-normal" title="Nullable">
                                            NULL
                                          </span>
                                        ) : (
                                          <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-normal" title="Not Nullable">
                                            NOT NULL
                                          </span>
                                        )}
                                        <button
                                          onClick={() => handleColumnEDA(selectedTable!, col)}
                                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
                                          title="View column analysis"
                                        >
                                          <BarChart3 size={14} className="text-blue-600 dark:text-blue-400" />
                                        </button>
                                      </div>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                              {previewData.preview?.slice(0, 100).map((row: any, idx: number) => (
                                <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                  {previewData.columns?.map((col: string) => (
                                    <td 
                                      key={col} 
                                      className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap max-w-xs truncate"
                                      title={row[col] !== null && row[col] !== undefined ? String(row[col]) : ''}
                                    >
                                      {row[col] !== null && row[col] !== undefined ? String(row[col]) : ''}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="p-8 text-center">
                        <Eye className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No preview available for this table.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Dependency Warning Modal */}
      {showDependencyModal && dependencyInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  <span>Dependency Warning</span>
                </h2>
                <button
                  onClick={() => setShowDependencyModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                The selected tables have dependencies on other tables that are not selected:
              </p>
              
              <ul className="space-y-2 mb-6">
                {dependencyInfo.missing_dependencies.map((dep: any, idx: number) => (
                  <li key={idx} className="flex items-start space-x-2 text-sm">
                    <span className="text-gray-400 dark:text-gray-500">•</span>
                    <span>
                      <strong className="text-gray-900 dark:text-gray-100">{dep.child_table}</strong>
                      <span className="text-gray-500 dark:text-gray-400"> depends on </span>
                      <strong className="text-gray-900 dark:text-gray-100">{dep.parent_table}</strong>
                      <span className="text-gray-400 dark:text-gray-500 text-xs block">
                        ({dep.child_column} → {dep.parent_column})
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDependencyModal(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleContinueAnyway}
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
                >
                  Continue Anyway
                </button>
                <button
                  onClick={handleAddDependencies}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Required Tables
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Column EDA Modal */}
      {showEDAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                <span>Column Analysis</span>
              </h2>
              <button
                onClick={() => setShowEDAModal(false)}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              {isLoadingEDA ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
                </div>
              ) : columnEDA ? (
                <div className="space-y-6">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.column_name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{edaTableName}</p>
                    </div>
                    <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium">
                      {columnEDA.data_type}
                    </span>
                  </div>

                  {/* Basic Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Total Values</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.total_count.toLocaleString()}</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Unique Values</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.unique_count.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{columnEDA.unique_percentage}%</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Null Values</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.null_count.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{columnEDA.null_percentage}%</p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">Non-Null</p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.non_null_count.toLocaleString()}</p>
                    </div>
                  </div>

                  {/* Numeric Stats */}
                  {columnEDA.is_numeric && (
                    <>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Statistical Summary</h4>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Min</p>
                            <p className="text-lg font-bold text-blue-900 dark:text-blue-300 truncate" title={`Minimum value: ${columnEDA.min?.toLocaleString()}`}>
                              {columnEDA.min?.toLocaleString()}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Minimum value: {columnEDA.min?.toLocaleString()}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">Max</p>
                            <p className="text-lg font-bold text-blue-900 dark:text-blue-300 truncate" title={`Maximum value: ${columnEDA.max?.toLocaleString()}`}>
                              {columnEDA.max?.toLocaleString()}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Maximum value: {columnEDA.max?.toLocaleString()}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center overflow-hidden group relative">
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">Mean</p>
                            <p className="text-lg font-bold text-green-900 dark:text-green-300 truncate" title={`Mean (average): ${columnEDA.mean?.toFixed(2)}`}>
                              {columnEDA.mean?.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Mean (average): {columnEDA.mean?.toFixed(2)}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-3 text-center overflow-hidden group relative">
                            <p className="text-xs text-green-600 dark:text-green-400 font-medium">Median</p>
                            <p className="text-lg font-bold text-green-900 dark:text-green-300 truncate" title={`Median (middle value): ${columnEDA.median?.toFixed(2)}`}>
                              {columnEDA.median?.toLocaleString(undefined, { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Median (middle value): {columnEDA.median?.toFixed(2)}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Std Dev</p>
                            <p className="text-lg font-bold text-purple-900 dark:text-purple-300 truncate" title={`Standard deviation: ${columnEDA.std?.toFixed(2)}`}>
                              {columnEDA.std?.toFixed(2)}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Standard deviation: {columnEDA.std?.toFixed(2)}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-3 text-center group relative">
                            <p className="text-xs text-purple-600 dark:text-purple-400 font-medium">Mode</p>
                            <p className="text-lg font-bold text-purple-900 dark:text-purple-300 truncate" title={`Mode (most frequent): ${columnEDA.mode && columnEDA.mode.length > 0 ? columnEDA.mode[0] : 'N/A'}`}>
                              {columnEDA.mode && columnEDA.mode.length > 0 ? columnEDA.mode[0] : 'N/A'}
                            </p>
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none">
                              <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-1 px-2 shadow-lg whitespace-nowrap">
                                Mode (most frequent): {columnEDA.mode && columnEDA.mode.length > 0 ? columnEDA.mode[0] : 'N/A'}
                                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                  <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Percentiles */}
                      {columnEDA.percentiles && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Percentiles</h4>
                          <div className="flex items-center space-x-1">
                            {Object.entries(columnEDA.percentiles).map(([key, value]) => (
                              <div key={key} className="flex-1 bg-gray-100 dark:bg-gray-700 rounded p-2 text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">{key.toUpperCase()}</p>
                                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value.toFixed(2)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Distribution */}
                      {columnEDA.histogram && (
                        <div>
                          <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Distribution</h4>
                          <div className="relative bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3" style={{ minHeight: '160px' }}>
                            {/* Y-axis grid lines - behind everything */}
                            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none" style={{ padding: '12px', zIndex: 0 }}>
                              {[0, 25, 50, 75, 100].map((percent) => (
                                <div
                                  key={percent}
                                  className="w-full border-t border-gray-300 dark:border-gray-600 opacity-30"
                                  style={{ marginTop: percent === 0 ? '0' : percent === 100 ? 'auto' : 'auto' }}
                                />
                              ))}
                            </div>
                            {/* Y-axis labels - behind bars */}
                            <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 pointer-events-none" style={{ padding: '12px 4px', zIndex: 1 }}>
                              {(() => {
                                const maxCount = Math.max(...columnEDA.histogram!.counts);
                                return [100, 75, 50, 25, 0].map((percent) => (
                                  <span key={percent}>{Math.round((maxCount * percent) / 100)}</span>
                                ));
                              })()}
                            </div>
                            {/* Bars - on top */}
                            <div className="flex items-end space-x-1 h-32 ml-8 relative" style={{ zIndex: 10 }}>
                              {columnEDA.histogram.counts.map((count, idx) => {
                                const maxCount = Math.max(...columnEDA.histogram!.counts);
                                const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                                const binStart = columnEDA.histogram!.bin_edges[idx];
                                const binEnd = columnEDA.histogram!.bin_edges[idx + 1];
                                const binRange = binEnd !== undefined 
                                  ? `${binStart.toFixed(2)} - ${binEnd.toFixed(2)}`
                                  : `${binStart.toFixed(2)}+`;
                                const percentage = maxCount > 0 ? ((count / maxCount) * 100).toFixed(1) : '0';
                                
                                return (
                                  <div
                                    key={idx}
                                    className="flex-1 group relative"
                                    style={{ height: '100%', display: 'flex', alignItems: 'flex-end' }}
                                  >
                                    <div
                                      className="w-full bg-blue-500 dark:bg-blue-600 rounded-t hover:bg-blue-600 dark:hover:bg-blue-500 transition-colors cursor-pointer"
                                      style={{ 
                                        height: `${height}%`, 
                                        minHeight: height > 0 ? '2px' : '0',
                                        width: '100%'
                                      }}
                                    />
                                    {/* Enhanced tooltip */}
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                                      <div className="bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
                                        <div className="font-semibold mb-1">Bin {idx + 1}</div>
                                        <div>Range: {binRange}</div>
                                        <div>Count: {count.toLocaleString()}</div>
                                        <div>Percentage: {percentage}%</div>
                                        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-full">
                                          <div className="border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {/* X-axis label */}
                            <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 ml-8">
                              Value Range (10 bins)
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Skewness & Kurtosis */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Skewness</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.skewness?.toFixed(3)}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {(columnEDA.skewness || 0) > 0.5 ? 'Right-skewed' : 
                             (columnEDA.skewness || 0) < -0.5 ? 'Left-skewed' : 'Approximately symmetric'}
                          </p>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400">Kurtosis</p>
                          <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{columnEDA.kurtosis?.toFixed(3)}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500">
                            {(columnEDA.kurtosis || 0) > 0 ? 'Heavy-tailed' : 'Light-tailed'}
                          </p>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Categorical Stats */}
                  {!columnEDA.is_numeric && columnEDA.top_values && (
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Top Values</h4>
                      <div className="space-y-2">
                        {columnEDA.top_values.slice(0, 10).map((item, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-xs" title={item.value}>
                                  {item.value}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">{item.count} ({item.percentage}%)</span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div
                                  className="bg-blue-500 dark:bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${item.percentage}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-center py-12">No data available</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Code Generation Loading Screen */}
      <ProcessLoadingScreen
        isVisible={isGeneratingCode}
        title="Generating Code"
        description="Creating Python code for synthetic data generation..."
        steps={[
          'Analyzing selected tables',
          'Checking dependencies',
          'Generating Python code',
          'Applying table relationships',
          'Finalizing code generation'
        ]}
        currentStep={codeGenStep}
      />
    </div>
  );
};

export default TableSelectionPage;
